/**
 * useDeskLens (TASK-924) — the Today desk lens.
 *
 * `DashboardPage.tsx` had no role check: Today was identical for the RM and the CO, and a
 * compliance officer opened the one attention surface onto a book of portfolio drift. The
 * lens is the fix, and it stays ON that surface — no new route, no CO dashboard.
 *
 * It is a LENS, not a permission. It changes what leads; `all` is always one click away, and
 * nothing here decides what a desk may see (that is `allowed_roles` on the server policy,
 * enforced in `signalPolicy.ts`). Which is why the default is a default and not a lock.
 *
 * Categories come from the server (`RadarResponse.categories`, the admitted catalogue keyed by
 * `source_event`), never from a table here — the same rule `action_policy` follows. A signal
 * and the task raised from it therefore resolve through one map and cannot land in different
 * chips.
 */
import { useCallback, useMemo } from 'react';
import { usePersistentChoice } from './usePersistentToggle';
import { useViewerRole } from '../contexts/AccountContext';
import { useRadar } from './useRadar';
import { useAlertStore } from '../stores/alertStore';
import { unresolvedAlerts } from '../utils/triageCounts';
import { SIGNAL_CATEGORIES, type SignalCategory } from '../services/radarService';

export type Lens = 'all' | SignalCategory;

export const LENSES: readonly Lens[] = ['all', ...SIGNAL_CATEGORIES] as const;

const LENS_STORAGE_KEY = 'kielis-rail-lens';

/** The chip labels, here rather than in `ChangeRadar` because the Do column names the lens too
 *  and the two must say the same word ("Documents", never the `document` key). */
export const LENS_LABEL: Record<Lens, string> = {
  all: 'All', portfolio: 'Portfolio', compliance: 'Compliance',
  client: 'Client', document: 'Documents', news: 'News',
};

/**
 * Does the Signals rail put anything on screen? TASK-783's rule: a book with nothing
 * outstanding should not take up morning space.
 *
 * Exported because the chip row is the lens's ONLY control, and it lives inside this rail.
 * `ChangeRadar` decides whether to render from this, and `useDeskLens` decides from the same
 * function whether the lens may bind at all — two copies of the predicate would drift, and the
 * drift shows up as a Do column filtered to a desk with no visible way back to `all`.
 */
export const signalsRailRenders = (
  events: unknown[], unresolved: unknown[], openAlerts: unknown[],
): boolean => events.length > 0 || unresolved.length > 0 || openAlerts.length > 0;

/** The desk each role opens on. Absent → `all`; the chip row is what any other desk sets by
 *  hand. TASK-924 deliberately adds no PM entry — that is parked with the portfolio work. */
const DEFAULT_LENS_BY_ROLE: Record<string, Lens> = {
  compliance_officer: 'compliance',
};

export interface DeskLens {
  lens: Lens;
  setLens: (next: Lens) => void;
  /**
   * The category of a signal, by its `source_event`. `undefined` means "this build cannot
   * say" — an unadmitted source, or a backend that predates `categories`. Callers must let
   * an unclassifiable row THROUGH: a lens that hides what it failed to identify hides work.
   */
  categoryOf: (sourceEvent: string | null | undefined) => SignalCategory | undefined;
  /** Does this row survive the current lens? True for every row while the lens is `all`. */
  matches: (sourceEvent: string | null | undefined) => boolean;
  /**
   * Is the chip row actually on screen? False when the Signals rail renders nothing, and a
   * lens with no control is a trap — it persists, so a reload does not clear it either.
   * `DashboardPage` withholds the lens from the Do column while this is false.
   */
  controllable: boolean;
}

export function useDeskLens(): DeskLens {
  const role = useViewerRole();
  const { data, isLoading } = useRadar();
  const alerts = useAlertStore((s) => s.alerts);

  const [lens, setLens] = usePersistentChoice<Lens>(
    LENS_STORAGE_KEY,
    LENSES,
    (role && DEFAULT_LENS_BY_ROLE[role]) || 'all',
  );

  const categories = data?.categories;
  const categoryOf = useCallback(
    (sourceEvent: string | null | undefined) =>
      (sourceEvent && categories?.[sourceEvent]) || undefined,
    [categories],
  );

  const matches = useCallback(
    (sourceEvent: string | null | undefined) => {
      if (lens === 'all') return true;
      const category = categoryOf(sourceEvent);
      // Unclassifiable → shown. See `categoryOf`.
      return category === undefined || category === lens;
    },
    [lens, categoryOf],
  );

  // Loading is not "empty": the rail is still on screen, so the lens still has its control.
  const controllable =
    isLoading ||
    signalsRailRenders(data?.events ?? [], data?.unresolved ?? [], unresolvedAlerts(alerts));

  return useMemo(
    () => ({ lens, setLens, categoryOf, matches, controllable }),
    [lens, setLens, categoryOf, matches, controllable],
  );
}
